/**
 * React Error Boundary
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs them, and displays a friendly fallback UI instead of a
 * blank white screen.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomePage />
 *   </ErrorBoundary>
 *
 * Or wrap specific sections:
 *   <ErrorBoundary fallback={<p>Chart failed to load</p>}>
 *     <ExpensiveChart />
 *   </ErrorBoundary>
 */

import React from 'react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // In production you'd send this to your error tracking service (e.g. Sentry)
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // Custom fallback if provided
    if (this.props.fallback) return this.props.fallback;

    const isDev = process.env.NODE_ENV === 'development';

    return (
      <div style={{
        padding: '40px 24px', maxWidth: 560, margin: '60px auto', textAlign: 'center',
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚡</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 10px' }}>
          Something went wrong
        </h2>
        <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: '0 0 24px' }}>
          An unexpected error occurred on this page. Your data is safe —
          this is a display error only.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={this.handleReset}
            style={{ padding: '10px 20px', background: '#1a2535', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Reload page
          </button>
        </div>

        {isDev && this.state.error && (
          <details style={{ marginTop: 24, textAlign: 'left' }}>
            <summary style={{ fontSize: 12, color: '#9ca3af', cursor: 'pointer', marginBottom: 8 }}>
              Error details (development only)
            </summary>
            <pre style={{ fontSize: 11, background: '#f9fafb', padding: 12, borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'auto', color: '#dc2626', lineHeight: 1.5 }}>
              {this.state.error.toString()}
              {this.state.errorInfo?.componentStack}
            </pre>
          </details>
        )}
      </div>
    );
  }
}
